import { CircleInfoIcon } from "@xola/icons";
import { flash, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { FormField, InputField } from "common";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { FacebookPixelTracking } from "types";
import { api } from "../../api/api";
import { ScrollToFieldError } from "../../helpers/validationHelper";
import { useSeller } from "../../hooks/useAccount";
import { useInvalidate } from "../../hooks/useInvalidation";
import { SecondaryButton } from "../Common/SecondaryButton";
import { XolaFormik } from "../Common/XolaFormik";
import { ContentLayout } from "../Layout/ContentLayout";
import { Loader } from "../Loader";

export const FacebookConversionTracking = () => {
    const [preferences, setPreferences] = useState<FacebookPixelTracking>();
    const seller = useSeller();
    const invalidate = useInvalidate();

    useEffect(() => {
        if (seller) {
            setPreferences({
                ...seller.preferences.facebookPixelTracking,
                facebookPixelId: seller.preferences.facebookPixelTracking.facebookPixelId ?? "",
                accessToken: seller.preferences.facebookPixelTracking.accessToken ?? "",
            });
        }
    }, [seller, setPreferences]);

    const { mutateAsync: update } = useMutation(async (payload: FacebookPixelTracking) => {
        try {
            await api.put(`/users/${seller.id}/preferences/facebookPixelTracking`, payload);
            invalidate("sellers", seller.id);
            flash.success("Successfully updated");
        } catch {
            flash.danger("Facebook conversion tracking is not saved.");
            return null;
        }
    });

    const onSave = async (values: FacebookPixelTracking) => {
        await update(values);
    };

    if (!preferences) {
        return <Loader />;
    }

    return (
        <XolaFormik enableReinitialize initialValues={preferences} onSubmit={onSave}>
            {({ values, handleSubmit, handleReset, setFieldValue, isSubmitting }) => {
                return (
                    <ContentLayout isWide isDisabled={isSubmitting}>
                        <ContentLayout.Section
                            title="Facebook"
                            description="Facebook Pixel allows you to report on purchase events that occur within your online checkout."
                        >
                            <FormField name="facebookPixelId" label="Facebook Pixel ID">
                                <InputField placeholder="Facebook Pixel ID" />
                            </FormField>

                            <FormField name="accessToken" label="Access Token">
                                <InputField placeholder="Access Token" />
                            </FormField>

                            <Switch.Group>
                                <Switch
                                    isChecked={values.allowAdvancedConversionTracking}
                                    size="large"
                                    onChange={(enabled: boolean) => {
                                        setFieldValue("allowAdvancedConversionTracking", enabled);
                                    }}
                                />

                                <Switch.Label direction="left" className="ml-3">
                                    Advanced matching
                                </Switch.Label>
                                <Tooltip content="Allow Xola to pass customer information to Facebook (name, email, phone). Facebook then uses this information to identify and re-market to customers">
                                    <CircleInfoIcon />
                                </Tooltip>
                            </Switch.Group>
                        </ContentLayout.Section>

                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isSubmitting} onClick={handleSubmit}>
                                Save
                            </SubmitButton>

                            <SecondaryButton type="reset" onClick={handleReset}>
                                Cancel
                            </SecondaryButton>
                        </ContentLayout.Footer>

                        <ScrollToFieldError />
                    </ContentLayout>
                );
            }}
        </XolaFormik>
    );
};
